import { faker } from "@faker-js/faker";
import { Factory } from "fishery";
import { RedactedPaymentPref, DetailedPaymentPref } from "..";
import { PaymentMethod } from "../common/enums";
import { WorkflowMode } from "../components/PaymentPrefConsolidated/common/enums";
import {
  DetailedAchPaymentPrefObjectType,
  DetailedWirePaymentPrefObjectType,
  DetailedCheckPaymentPrefObjectType,
  Position,
} from "../components/PaymentPrefConsolidated/common/types";
import { CheckPaymentPrefInputType } from "../components/PaymentPrefConsolidated/Components/CheckPaymentPrefInput/constants";
import { getBankInfoRedacted } from "../components/PaymentPrefConsolidated/Components/CheckPaymentPrefInput/utils";
import {
  AccountPositionObjectType,
  ConsolidatedPaymentPrefContextType,
  PaymentMethodValue,
  PositionEdge,
  PositionConnection,
  AccountObjectType,
  AccountEdge,
} from "../components/PaymentPrefConsolidated/Context/types";
import { WireBankFullFragment } from "../graphql/supergraph.index";
import { toGlobalId } from "../utils/graphql_utils";
import { subscriptionAddressFactory } from "./subscriptionAddress.factory";

export const consolidatedPaymentPrefPositionFactory: Factory<Position> =
  Factory.define<Position>(({ params, sequence }) => ({
    id: params.id ?? toGlobalId("position", sequence),
    investmentEntityId: params.investmentEntityId ?? toGlobalId("investmentEntity", sequence),
    investmentEntityName: params.investmentEntityName ?? "Some entity name",
    accountId: params.accountId ?? toGlobalId("account", sequence),
  }));

export const paymentMethodValueFactory = Factory.define<PaymentMethodValue>(() => ({
  isAvailable: true,
  warning: "",
}));

export const paymentMethodsFactory = Factory.define<Record<PaymentMethod, PaymentMethodValue>>(
  () => ({
    [PaymentMethod.ACH]: paymentMethodValueFactory.build(),
    [PaymentMethod.Wire]: paymentMethodValueFactory.build(),
    [PaymentMethod.Check]: paymentMethodValueFactory.build(),
  }),
);

export const consolidatedPaymentPrefContextFactory =
  Factory.define<ConsolidatedPaymentPrefContextType>(() => ({
    setAssociatePaymentPref: () => undefined,
    setSelectAvailablePaymentPrefsFormHeaderComponent: () => undefined,
    setListOfPositionsToUpdateComponent: () => undefined,
    paymentMethods: paymentMethodsFactory.build(),
    setSelectedPaymentMethod: () => undefined,
    setPaymentPrefDetail: () => undefined,
    setSelectedPaymentPref: () => undefined,
    isExistingPaymentPref: false,
    setIsExistingPaymentPref: () => undefined,
    setPaymentPrefId: () => undefined,
    setUpdatedPositionsPerAccount: () => undefined,
    workflowMode: WorkflowMode.Create,
  }));
export const investmentEntityFactory = Factory.define<
  AccountPositionObjectType["investmentEntity"]
>(({ sequence }) => ({
  __typename: "AccountObjectType",
  id: toGlobalId("AccountObjectType", sequence),
  name: faker.company.name(),
}));
export const positionObjectTypeFactory = Factory.define<AccountPositionObjectType>(
  ({ sequence }) => ({
    __typename: "PositionObjectType",
    id: toGlobalId("PositionObjectType", sequence),
    investmentEntity: investmentEntityFactory.build(),
  }),
);

export const positionEdgeFactory = Factory.define<PositionEdge>(() => ({
  __typename: "PositionEdge",
  node: positionObjectTypeFactory.build(),
}));

export const positionConnectionFactory = Factory.define<PositionConnection>(({ params }) => ({
  __typename: "PositionConnection",
  count: 2,
  edges: positionEdgeFactory.buildList(
    params?.count !== undefined && params?.count !== null ? params.count : 2,
  ),
}));

export const accountObjectTypeFactory = Factory.define<AccountObjectType>(({ sequence }) => ({
  __typename: "AccountObjectType",
  id: toGlobalId("AccountObjectType", sequence),
  name: faker.company.name(),
  canViewAccountDetails: false,
  canEditAccountDetails: false,
  canLpcmManageContacts: false,
  canAccessLpcm: false,
  isInvestmentEntity: false,
  gpDisplayName: faker.company.buzzNoun(),
  taxIdRedacted: null,
  arenaId: 1,
  arena: null,
  contacts: null,
  mailingAddress: null,
  paymentPrefs: [],
  paymentMethodSupportedCounts: {
    ach: 0,
    wire: 0,
    check: 0,
  },
  positions: positionConnectionFactory.build(),
}));

export const accountEdgeFactory = Factory.define<AccountEdge>(() => ({
  __typename: "AccountEdge",
  node: accountObjectTypeFactory.build(),
}));

class RedactedPaymentPrefFactory extends Factory<RedactedPaymentPref> {
  ach() {
    return this.params({
      paymentMethod: PaymentMethod.ACH,
      bankInfoRedacted: "Bank of America ACH --redacted",
    });
  }

  wire() {
    return this.params({
      paymentMethod: PaymentMethod.Wire,
      bankInfoRedacted: "Bank of America Wire --redacted",
    });
  }

  check() {
    return this.params({
      paymentMethod: PaymentMethod.Check,
      bankInfoRedacted: "Bank of America Check --redacted",
    });
  }

  verificationError() {
    return this.params({
      verificationStatus: "error",
    });
  }

  unverified() {
    return this.params({
      verificationStatus: "unverified",
    });
  }
}

export const redactedPaymentPrefFactory = RedactedPaymentPrefFactory.define(({ sequence }) => {
  return {
    __typename: "PaymentPrefObjectType" as const,
    id: toGlobalId("PaymentPrefObjectType", sequence),
    paymentMethod: PaymentMethod.ACH,
    bankInfoRedacted: "redacted bank info details",
    verificationStatus: null,
  };
});

export interface CheckPaymentPrefInputTypeFactoryTransientParams {
  state?: string | null;
  province?: string | null;
  country?: string | null;
}

export const checkPaymentPrefInputTypeFactory = Factory.define<
  CheckPaymentPrefInputType,
  CheckPaymentPrefInputTypeFactoryTransientParams
>(({ transientParams }) => {
  return {
    memo: "",
    payeeName: "Tark Smithbrow",
    address: {
      city: "San Francisco",
      country: transientParams?.country ?? "United States:Q291bnRyeU9iamVjdFR5cGU6MjM0",
      province: transientParams?.province ?? undefined,
      sameAsMailingAddress: false,
      state: transientParams?.state ?? "CA",
      streetLine1: "123 Street",
      streetLine2: "Apartment 4",
      zip: "94104",
    },
  };
});

export const detailedAchPaymentPrefsFactory = Factory.define<DetailedAchPaymentPrefObjectType>(
  ({ sequence, params }) => {
    const paymentPrefId = params?.paymentPrefId ?? sequence;
    return {
      __typename: "AchPaymentPrefObjectType",
      id: toGlobalId("PaymentPrefObjectType", paymentPrefId),
      paymentPrefId,
      bankName: "Bank of America",
      routingNumber: "111000038",
      accountNumber: "011111111",
      accountName: "John Doe",
      accountType: "checking",
      accountOwnership: "business",
      additionalInstructions: "additional instructions",
      accountHolderType: "legal_entity",
      address: subscriptionAddressFactory.build(),
      bankInfoRedacted: "Bank of America --redacted",
      naturalPersonDateOfBirth: "1990-01-01",
      naturalPersonEmailAddress: "natural@person.com",
      naturalPersonFirstName: "John",
      naturalPersonLastName: "Doe",
      naturalPersonMiddleName: "M.",
      paymentMethod: PaymentMethod.ACH,
      signatureFullName: "signature full name",
    };
  },
);

class WireBankFullFragmentFactory extends Factory<WireBankFullFragment> {
  intermediary() {
    return this.params({
      name: "Intermediary Test Bank",
      abaNumber: "987654321",
      swiftCode: "abcdefgh123",
      intlRoutingCode: "123",
    });
  }
}

export const wireBankFullFragmentFactory = WireBankFullFragmentFactory.define(
  ({ associations }) => {
    return {
      name: "Test Bank",
      address: associations.address ?? subscriptionAddressFactory.build(),
      abaNumber: "123456780",
      swiftCode: "TESTSWFT",
      intlRoutingCode: "INTL123",
    };
  },
);
export const detailedWirePaymentPrefsFactory =
  Factory.define<DetailedWirePaymentPrefObjectType>(({ sequence, associations }) => {
    return {
      __typename: "WirePaymentPrefObjectType",
      paymentMethod: PaymentMethod.Wire,
      id: toGlobalId("PaymentPrefObjectType", sequence),
      paymentPrefId: sequence,
      beneficiaryName: "Beneficiary Name",
      beneficiaryAddress: associations.beneficiaryAddress ?? subscriptionAddressFactory.build(),
      beneficiaryAccountNumber: "123456789",
      beneficiaryType: "unknown",
      referenceToBeneficiary: "reference",
      wireObi: {
        __typename: "WireExtraFieldObjectType",
        field0: "Obi line1",
        field1: "Obi line2",
        field2: "Obi line3",
        field3: "Obi line4",
      },
      wireBbi: {
        __typename: "WireExtraFieldObjectType",
        field0: "",
        field1: "",
        field2: "",
        field3: "",
      },
      bank: associations.bank ?? wireBankFullFragmentFactory.build(),
      intermediaryBank:
        associations.intermediaryBank ?? wireBankFullFragmentFactory.intermediary().build(),
      bankInfoRedacted: "Bank of America --redacted",
      naturalPersonDateOfBirth: "1990-01-01",
      naturalPersonEmailAddress: "natural@person.com",
      naturalPersonFirstName: "John",
      naturalPersonLastName: "Doe",
      naturalPersonMiddleName: "M.",
    };
  });
class DetailedCheckPaymentPrefsFactory extends Factory<DetailedCheckPaymentPrefObjectType> {
  usa() {
    return this.params({
      address: subscriptionAddressFactory.california().build(),
    });
  }

  australia() {
    return this.params({
      address: subscriptionAddressFactory.australia().build(),
    });
  }
}

export const detailedCheckPaymentPrefsFactory = DetailedCheckPaymentPrefsFactory.define(
  ({ sequence, associations, params }) => {
    const address = associations?.address ?? subscriptionAddressFactory.build();
    const payeeName = params.payeeName || "Random name";
    return {
      __typename: "CheckPaymentPrefObjectType" as const,
      paymentMethod: PaymentMethod.Check,
      id: toGlobalId("PaymentPrefObjectType", sequence),
      paymentPrefId: sequence,
      payeeName,
      address,
      useAccountMailingAddress: false,
      accountHolderType: null,
      naturalPersonFirstName: null,
      naturalPersonMiddleName: null,
      naturalPersonLastName: null,
      naturalPersonDateOfBirth: null,
      naturalPersonEmailAddress: null,
      bankName: null,
      bankCountry: null,
      memo: "",
      bankInfoRedacted: getBankInfoRedacted(payeeName, address),
    };
  },
);

class DetailedPaymentPrefFactory extends Factory<DetailedPaymentPref> {
  ach() {
    return this.params({
      paymentMethod: PaymentMethod.ACH,
    }).associations({
      paymentPrefFull: detailedAchPaymentPrefsFactory.build(),
    });
  }

  wire() {
    return this.params({
      paymentMethod: PaymentMethod.Wire,
    }).associations({
      paymentPrefFull: detailedWirePaymentPrefsFactory.build(),
    });
  }

  check() {
    return this.params({
      paymentMethod: PaymentMethod.Check,
    }).associations({
      paymentPrefFull: detailedCheckPaymentPrefsFactory.build(),
    });
  }
}

export const detailedPaymentPrefFactory = DetailedPaymentPrefFactory.define(
  ({ sequence, associations }) => {
    return {
      __typename: "PaymentPrefObjectType" as const,
      id: toGlobalId("PaymentPrefObjectType", sequence),
      bankInfoRedacted: null,
      checkAddrCity: null,
      checkAddrCountry: null,
      checkAddrCountryId: null,
      checkAddrPostalCode: null,
      checkAddrRegion: null,
      checkAddrState: null,
      checkAddrStateId: null,
      checkAddrStreet: null,
      checkAddrStreet2: null,
      checkAddrStreet3: null,
      checkMailingAddressSameAsAccount: null,
      checkMemoField: null,
      checkPayeeOverride: null,
      paymentMethod: null,
      paymentPrefFull: associations?.paymentPrefFull ?? null,
      wireHasIntermediary: null,
      wireHasInternationalBeneficiaryAddress: null,
      verificationStatus: null,
    };
  },
);
